import React, { useState } from "react";
import {
  Row,
  Col,
  Select,
  Input,
  Slider,
  Button,
  Tag,
  Typography,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  EnvironmentOutlined,
  HomeOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import "./FilterLeads.css";
// Ensure this is the correct path to your axios instance
import axiosNew from "../../store/api/axiosNew";

// Static local data
import { INDIA_DATA } from "../../constants/indiaData";

const { Title } = Typography;

const landTypes = [
  "Residential Plot",
  "Commercial Land",
  "Agri/Farm Land",
  "Industrial Land",
  "Villa",
  "House",
  "Apartment",
];

export default function FilterLeads() {
  const navigate = useNavigate();

  // Core States
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedArea, setSelectedArea] = useState("");
  const [pincode, setPincode] = useState("");
  const [budget, setBudget] = useState([100000, 10000000]);
  const [selectedLandTypes, setSelectedLandTypes] = useState([]);

  // --- HANDLERS ---

  const handleStateChange = (val) => {
    // Handling AntD tags mode: usually we want the string value
    const value = Array.isArray(val) ? val[val.length - 1] : val;
    setSelectedState(value);
    setSelectedDistrict(null);
    setSelectedArea("");
  };

  const handleDistrictChange = (val) => {
    const value = Array.isArray(val) ? val[val.length - 1] : val;
    setSelectedDistrict(value);
    setSelectedArea("");
  };

  const toggleLandType = (type) => {
    setSelectedLandTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  // --- REAL API CALL ---
  const handleApply = async () => {
    const token = localStorage.getItem("accessToken");
    const isLoggedIn = !!token;

    const hide = message.loading("Fetching filtered leads...", 0);

    try {
      const baseURL = "https://stageapilead.blaccdot.com/api/admin/all-leads";

      // 1. Build dynamic query parameters based on user selection
      const params = {};
      if (selectedArea) params.search = selectedArea;

      // Mapping local state 'selectedLandTypes' to API param 'category'
      if (selectedLandTypes.length > 0) {
        params.category = selectedLandTypes.join(",");
      }

      if (budget?.length === 2) {
        params.minBudget = budget[0];
        params.maxBudget = budget[1];
      }

      if (selectedState)
        params.state = Array.isArray(selectedState)
          ? selectedState.join(",")
          : selectedState;
      if (selectedDistrict)
        params.district = Array.isArray(selectedDistrict)
          ? selectedDistrict.join(",")
          : selectedDistrict;
      if (pincode) params.pincode = pincode;

      // 2. Execute Request
      const res = await axiosNew.get(baseURL, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // 3. Extract data (Assuming structure res.data.data is the array of leads)
      const realLeads = res.data?.data || [];

      hide();

      if (realLeads.length === 0) {
        message.info("No leads found matching your criteria.");
      } else {
        message.success(`${realLeads.length} leads loaded!`);
      }

      // Determine destination
      const targetPath = isLoggedIn ? "/user-dashboard" : "/retail-leads";

      // 4. Navigate and pass both the original filters and the fetched results
      navigate(targetPath, {
        state: {
          filters: {
            state: selectedState,
            district: selectedDistrict,
            area: selectedArea,
            pincode,
            landType: selectedLandTypes,
            budget,
          },
          results: realLeads,
        },
      });
    } catch (err) {
      hide();
      console.error("Filter Error:", err);
      message.error(
        err.response?.data?.message || "Failed to fetch data from server",
      );
    }
  };

  const toLakhs = (val) =>
    val >= 10000000
      ? `${(val / 10000000).toFixed(1)} Cr`
      : `${(val / 100000).toFixed(1)} L`;

  return (
    <div
      className="filter-card"
      style={{
        padding: "24px",
        background: "#fff",
        maxWidth: "1200px",
        margin: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <Title level={3}>Notify Leads</Title>
        <Button onClick={() => navigate(-1)} danger ghost>
          Back
        </Button>
      </div>

      <div
        style={{
          border: "1px solid #f0f0f0",
          padding: "24px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <Title level={5}>
          <EnvironmentOutlined /> Location Details
        </Title>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <label>State</label>
            <Select
              showSearch
              mode="tags"
              placeholder="Select or Type State"
              style={{ width: "100%" }}
              value={selectedState}
              onChange={handleStateChange}
              options={Object.keys(INDIA_DATA).map((s) => ({
                label: s,
                value: s,
              }))}
              maxTagCount={1}
            />
          </Col>

          <Col xs={24} md={6}>
            <label>District</label>
            <Select
              showSearch
              mode="tags"
              placeholder="Select or Type District"
              style={{ width: "100%" }}
              value={selectedDistrict}
              onChange={handleDistrictChange}
              options={
                selectedState && INDIA_DATA[selectedState]
                  ? INDIA_DATA[selectedState].map((d) => ({
                      label: d,
                      value: d,
                    }))
                  : []
              }
              maxTagCount={1}
            />
          </Col>

          <Col xs={24} md={6}>
            <label>Address / Area</label>
            <Input
              placeholder="Enter Address / Area"
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              allowClear
            />
          </Col>

          <Col xs={24} md={6}>
            <label>Pincode</label>
            <Input
              placeholder="Enter Pincode"
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              maxLength={6}
            />
          </Col>
        </Row>

        <Title level={5} style={{ marginTop: 32 }}>
          <HomeOutlined /> Land Type
        </Title>

        <div
          style={{
            background: "#fafafa",
            padding: "12px",
            borderRadius: "8px",
          }}
        >
          {landTypes.map((type) => (
            <Tag.CheckableTag
              key={type}
              checked={selectedLandTypes.includes(type)}
              onChange={() => toggleLandType(type)}
              style={{
                border: "1px solid #d9d9d9",
                padding: "6px 16px",
                marginBottom: "8px",
                fontSize: "14px",
                borderRadius: "4px",
              }}
            >
              {type}
            </Tag.CheckableTag>
          ))}
        </div>

        <Title level={5} style={{ marginTop: 32 }}>
          <DollarOutlined /> Budget Range
        </Title>

        <div style={{ padding: "0 10px" }}>
          <Slider
            range
            min={100000}
            max={100000000}
            step={100000}
            value={budget}
            onChange={setBudget}
            tooltip={{ formatter: toLakhs }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              color: "#888",
            }}
          >
            <span>Min: {toLakhs(budget[0])}</span>
            <span>Max: {toLakhs(budget[1])}</span>
          </div>
        </div>

        <Button
          type="primary"
          onClick={handleApply}
          block
          style={{
            background: "blue",
            border: "none",
            marginTop: 40,
            height: "45px",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: "bold",
            width: "100%",
          }}
        >
          Submit your lead preference
        </Button>
      </div>
    </div>
  );
}
